// WhatsApp Business API Service Layer
// Handles all interactions with the WhatsApp Business API

import { validateForDeployment, FLOW_JSON_VERSION } from '../utils/flowValidation';

const API_VERSION = 'v25.0';

// Check if backend URL is configured (for demo mode with proxy)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const USE_BACKEND_PROXY = !!BACKEND_URL;

// Use backend proxy if configured, otherwise use Facebook Graph API directly
const FACEBOOK_GRAPH_API_BASE = USE_BACKEND_PROXY
  ? `${BACKEND_URL}/api/whatsapp`
  : `https://graph.facebook.com/${API_VERSION}`;

console.log('WhatsApp API Service Configuration:', {
  useBackendProxy: USE_BACKEND_PROXY,
  baseUrl: FACEBOOK_GRAPH_API_BASE,
  mode: USE_BACKEND_PROXY ? 'DEMO MODE (via proxy)' : 'DIRECT MODE (with token)'
});

// Enhanced error class that preserves WhatsApp API error details
class APIError extends Error {
  constructor(message, code = 'UNKNOWN', fullErrorData = null) {
    super(message);
    this.name = 'APIError';
    this.code = code;
    this.fullErrorData = fullErrorData;
  }
}

// Lightweight rate limiter
class SimpleRateLimiter {
  constructor(requestsPerMinute = 100) {
    this.requests = [];
    this.limit = requestsPerMinute;
  }

  async wait() {
    const now = Date.now();
    this.requests = this.requests.filter(time => now - time < 60000);
    
    if (this.requests.length >= this.limit) {
      const waitTime = 60000 - (now - this.requests[0]);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
    
    this.requests.push(now);
  }
}

// Base API client with common functionality
class BaseAPIClient {
  constructor(accessToken) {
    this.accessToken = accessToken;
    this.baseURL = FACEBOOK_GRAPH_API_BASE;
    this.rateLimiter = new SimpleRateLimiter(100);
  }

  getHeaders() {
    const headers = {
      'Content-Type': 'application/json'
    };

    // Only add Authorization header if NOT using backend proxy
    // Backend proxy adds the token server-side
    if (!USE_BACKEND_PROXY && this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    return headers;
  }

  async handleResponse(response) {
    const data = await response.json();
    if (!response.ok) {
      throw new APIError(
        data.error?.message || 'Request failed',
        data.error?.code || response.status,
        data.error // Pass the full error data for detailed error handling
      );
    }
    return data;
  }

  async request(endpoint, method = 'GET', data = null, params = null) {
    await this.rateLimiter.wait();
    
    // Build URL with query parameters if provided
    let url = `${this.baseURL}${endpoint}`;
    if (params) {
      const urlParams = new URLSearchParams(params);
      url += `?${urlParams.toString()}`;
    }
    
    const options = {
      method,
      headers: this.getHeaders(),
      ...(data && { body: JSON.stringify(data) })
    };

    try {
      const response = await fetch(url, options);
      return this.handleResponse(response);
    } catch (error) {
      if (error instanceof APIError) {
        throw error;
      }
      throw new APIError(`Network error: ${error.message}`, 'NETWORK_ERROR');
    }
  }
}

// WhatsApp Business API service extending base client
class WhatsAppBusinessAPIService extends BaseAPIClient {
  constructor(accessToken) {
    super(accessToken);
  }

  // Business Account Methods
  async getBusinessAccounts() {
    return this.request('/me/businesses');
  }

  async getWhatsAppBusinessAccounts(businessId) {
    return this.request(`/${businessId}/client_whatsapp_business_accounts`);
  }

  // Phone Number Methods
  async getPhoneNumbers(wabaId) {
    return this.request(`/${wabaId}/phone_numbers`);
  }

  async getPhoneNumberDetails(phoneNumberId) {
    return this.request(`/${phoneNumberId}`);
  }

  // Flow Management Methods
  async getFlows(wabaId) {
    return this.request(`/${wabaId}/flows`, 'GET', null, {
      fields: 'id,name,status,categories'
    });
  }

  async getFlow(flowId) {
    return this.request(`/${flowId}`, 'GET', null, {
      fields: 'id,name,status,categories'
    });
  }

  // Get flow assets (including download URL for flow JSON)
  async getFlowAssets(flowId) {
    return this.request(`/${flowId}/assets`);
  }

  // Download flow JSON content from asset URL
  async downloadFlowJson(downloadUrl) {
    console.log('Attempting to download flow JSON from URL:', downloadUrl);

    // If using backend proxy, use the /download endpoint to bypass CORS
    if (USE_BACKEND_PROXY) {
      try {
        console.log('Using backend proxy to download flow JSON...');
        const response = await fetch(`${BACKEND_URL}/api/whatsapp/download`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ url: downloadUrl })
        });

        if (!response.ok) {
          throw new Error(`Download failed: ${response.statusText}`);
        }

        const flowJson = await response.text();
        console.log('Successfully downloaded flow JSON via backend proxy');
        return JSON.parse(flowJson);
      } catch (error) {
        console.error('Error downloading flow JSON via backend:', error);
        throw new APIError(`Failed to download flow JSON: ${error.message}`, 'DOWNLOAD_ERROR');
      }
    }

    // If not using backend proxy, CORS will prevent direct download
    console.warn('Browser cannot access WhatsApp CDN URLs due to CORS restrictions.');
    console.info('Flow JSON download requires backend proxy. Set REACT_APP_BACKEND_URL to enable.');

    return null; // Return null since browser access is not possible without proxy
  }


  async createFlow(wabaId, flowData) {
    const payload = {
      name: flowData.name,
      categories: flowData.categories || ['OTHER'],
      ...(flowData.flow_json && { flow_json: flowData.flow_json }),
      ...(flowData.endpoint_uri && { endpoint_uri: flowData.endpoint_uri }),
      ...(flowData.preview && { preview: flowData.preview })
    };
    return this.request(`/${wabaId}/flows`, 'POST', payload);
  }

  async updateFlow(flowId, flowData) {
    const payload = {
      ...(flowData.name && { name: flowData.name }),
      ...(flowData.categories && { categories: flowData.categories }),
      ...(flowData.flow_json && { flow_json: flowData.flow_json }),
      ...(flowData.endpoint_uri && { endpoint_uri: flowData.endpoint_uri }),
      ...(flowData.preview && { preview: flowData.preview })
    };
    return this.request(`/${flowId}`, 'POST', payload);
  }

  async deleteFlow(flowId) {
    return this.request(`/${flowId}`, 'DELETE');
  }

  async publishFlow(flowId) {
    return this.request(`/${flowId}/publish`, 'POST');
  }

  async deprecateFlow(flowId) {
    return this.request(`/${flowId}/deprecate`, 'POST');
  }

  // Flow Asset Methods
  async uploadFlowAsset(wabaId, file, assetType = 'FLOW_ASSET') {
    await this.rateLimiter.wait();

    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', assetType);
    formData.append('messaging_product', 'whatsapp');

    const headers = {};

    // Only add Authorization header if NOT using backend proxy
    if (!USE_BACKEND_PROXY && this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    const response = await fetch(`${this.baseURL}/${wabaId}/assets`, {
      method: 'POST',
      headers,
      body: formData
    });

    return this.handleResponse(response);
  }

  // Analytics and Insights
  async getFlowInsights(flowId, metrics = ['flow_total_visits']) {
    const metricsParam = metrics.join(',');
    return this.request(`/${flowId}/insights?metric=${metricsParam}`);
  }

  // Utility Methods
  async testConnection() {
    try {
      const result = await this.request('/me');
      return { success: true, user: result };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
}

// Simplified flow utilities
export const flowHelpers = {
  // Convert to WhatsApp format
  convertToWhatsAppFlow: (internalFlow, flowName) => ({
    name: flowName,
    categories: ['OTHER'],
    flow_json: typeof internalFlow === 'string' ? internalFlow : JSON.stringify(internalFlow)
  }),

  // Convert from WhatsApp format
  convertFromWhatsAppFlow: (whatsappFlow) => ({
    id: whatsappFlow.id,
    name: whatsappFlow.name,
    status: whatsappFlow.status,
    categories: whatsappFlow.categories,
    flowJson: typeof whatsappFlow.flow_json === 'string' 
      ? JSON.parse(whatsappFlow.flow_json) 
      : whatsappFlow.flow_json,
    createdTime: whatsappFlow.created_time,
    updatedTime: whatsappFlow.updated_time
  }),

  // Validate flow for deployment
  validateBeforeDeployment: (flowJson) => {
    try {
      const flow = typeof flowJson === 'string' ? JSON.parse(flowJson) : flowJson;
      const validation = validateForDeployment(flow);
      
      if (flow.version !== FLOW_JSON_VERSION) {
        validation.warnings.push(`Flow version is ${flow.version}, WhatsApp recommends version ${FLOW_JSON_VERSION}`);
      }

      return validation;
    } catch (error) {
      return {
        isValid: false,
        errors: [`Invalid flow JSON: ${error.message}`],
        warnings: []
      };
    }
  }
};

export { WhatsAppBusinessAPIService, APIError };